import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useProjectRoles,
  useCreateProjectRole,
  useUpdateProjectRole,
  useDeleteProjectRole,
} from '../../hooks/useLeads';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { ConfirmModal } from '../../components/common/Modal';

export default function ProjectRolePage() {
  const navigate = useNavigate();
  const { data: rolesData, isLoading } = useProjectRoles();
  const createMutation = useCreateProjectRole();
  const updateMutation = useUpdateProjectRole();
  const deleteMutation = useDeleteProjectRole();

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingCode, setEditingCode] = useState('');

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const roles = rolesData?.data ?? [];

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    try {
      await createMutation.mutateAsync({ name: name.trim(), code: code.trim() || undefined });
      setShowForm(false);
      setName('');
      setCode('');
    } catch (err) {
      setFormError((err as Error).message);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId === null) return;
    try {
      await updateMutation.mutateAsync({
        id: editingId,
        name: editingName.trim(),
        code: editingCode.trim() || null,
      });
      setEditingId(null);
    } catch (err) {
      setFormError((err as Error).message);
    }
  };

  const handleDelete = async () => {
    if (deleteId === null) return;
    setDeleteError(null);
    try {
      await deleteMutation.mutateAsync(deleteId);
      setDeleteId(null);
    } catch (err) {
      setDeleteError((err as Error).message);
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-8"><Spinner /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <button onClick={() => navigate('/admin')} className="text-sm text-gray-500 hover:text-gray-700 mb-1">
          &larr; Back to Admin
        </button>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-gray-900">Project Roles</h1>
          <Button size="sm" onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Cancel' : 'Add Role'}
          </Button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
          {formError && <p className="text-sm text-red-600">{formError}</p>}
          <div className="flex gap-3 items-center">
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Role name *"
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Code (e.g. Dev)"
              className="w-36 rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <Button type="submit" size="sm" isLoading={createMutation.isPending}>Create</Button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">References</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {roles.map((role) => (
              <tr key={role.id} className="hover:bg-gray-50">
                {editingId === role.id ? (
                  <>
                    <td className="px-6 py-2" colSpan={3}>
                      <form onSubmit={handleUpdate} className="flex gap-2 items-center">
                        <input
                          type="text"
                          value={editingCode}
                          onChange={(e) => setEditingCode(e.target.value)}
                          placeholder="Code"
                          className="w-24 rounded border border-gray-300 px-2 py-1 text-sm"
                          autoFocus
                        />
                        <input
                          type="text"
                          required
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
                        />
                        <Button type="submit" size="sm" isLoading={updateMutation.isPending}>Save</Button>
                        <Button type="button" size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
                      </form>
                    </td>
                    <td></td>
                  </>
                ) : (
                  <>
                    <td className="px-6 py-3 text-sm font-mono text-gray-500">{role.code ?? '—'}</td>
                    <td className="px-6 py-3 text-sm font-medium text-gray-900">{role.name}</td>
                    <td className="px-6 py-3 text-sm text-gray-500">{role.referenceCount ?? 0}</td>
                    <td className="px-6 py-3 text-right space-x-3">
                      <button
                        onClick={() => {
                          setEditingId(role.id);
                          setEditingName(role.name);
                          setEditingCode(role.code ?? '');
                        }}
                        className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => { setDeleteId(role.id); setDeleteError(null); }}
                        disabled={(role.referenceCount ?? 0) > 0}
                        className={`text-sm font-medium ${
                          (role.referenceCount ?? 0) > 0
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-red-600 hover:text-red-800'
                        }`}
                        title={(role.referenceCount ?? 0) > 0 ? 'Cannot delete — role is in use' : 'Delete role'}
                      >
                        Delete
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
            {roles.length === 0 && (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-400">
                  No project roles yet. Click &ldquo;Add Role&rdquo; to create one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Project Role"
        message="Are you sure you want to delete this role? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
        error={deleteError}
      />
    </div>
  );
}
